import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import {
  ArrowLeft,
  FileBox,
  Upload,
  Download,
  Eye,
  ArrowRightLeft,
  Shield,
  CheckCircle,
  User,
  Clock,
  Monitor,
} from 'lucide-react';
import { format } from 'date-fns';

const EVENT_ICONS: Record<string, typeof Upload> = {
  upload: Upload,
  download: Download,
  view: Eye,
  transfer: ArrowRightLeft,
  legal_hold: Shield,
  integrity_check: CheckCircle,
};

const EVENT_COLORS: Record<string, string> = {
  upload: 'bg-green-100 text-green-600',
  download: 'bg-blue-100 text-blue-600',
  view: 'bg-gray-100 text-gray-600',
  transfer: 'bg-purple-100 text-purple-600',
  legal_hold: 'bg-amber-100 text-amber-600',
  integrity_check: 'bg-emerald-100 text-emerald-600',
};

export default function CustodyChain() {
  const { evidenceId } = useParams<{ evidenceId: string }>();

  const { data: evidence, isLoading: evidenceLoading } = useQuery({
    queryKey: ['evidence', evidenceId],
    queryFn: () => apiClient.getEvidence(evidenceId!),
    enabled: !!evidenceId,
  });

  const { data: custodyChain, isLoading: custodyLoading } = useQuery({
    queryKey: ['custody-chain', evidenceId],
    queryFn: () => apiClient.getEvidenceCustodyChain(evidenceId!),
    enabled: !!evidenceId,
  });

  const isLoading = evidenceLoading || custodyLoading;

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading custody chain...</p>
      </div>
    );
  }

  if (!evidence) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Evidence not found</p>
        <Link to="/evidence" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
          ← Back to evidence
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={`/cases/${evidence.caseNumber}`}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to case
      </Link>

      {/* Evidence header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gray-100 rounded-lg">
            <FileBox className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{evidence.fileName}</h1>
            <p className="text-gray-500">
              Case {evidence.caseNumber} • {evidence.evidenceType}
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">SHA-256 Hash (Integrity Signature)</p>
          <p className="font-mono text-xs break-all text-gray-700">{evidence.sha256Hash}</p>
        </div>
      </div>

      {/* Custody timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Chain of Custody</h2>
          <p className="text-sm text-gray-500 mt-1">
            Complete audit trail of all evidence interactions
          </p>
        </div>

        {!custodyChain?.length ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No custody events recorded
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

            <div className="divide-y divide-gray-200">
              {custodyChain.map((event, index) => {
                const Icon = EVENT_ICONS[event.eventType] ?? Eye;
                const colorClass = EVENT_COLORS[event.eventType] ?? 'bg-gray-100 text-gray-600';

                return (
                  <div key={event.id} className="relative px-6 py-4">
                    <div className="flex items-start gap-4 ml-4">
                      {/* Icon */}
                      <div
                        className={`relative z-10 flex-shrink-0 p-2 rounded-full ${colorClass}`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900 capitalize">
                              {event.eventType.replace('_', ' ')}
                            </p>
                            <p className="text-gray-600 mt-1">{event.description}</p>
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                            #{custodyChain.length - index}
                          </span>
                        </div>

                        {/* Metadata */}
                        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {event.performedByName}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {format(new Date(event.performedAt), 'MMM d, yyyy h:mm:ss a')}
                          </span>
                          {event.ipAddress && (
                            <span className="flex items-center gap-1">
                              <Monitor className="h-4 w-4" />
                              {event.ipAddress}
                            </span>
                          )}
                        </div>

                        {/* Hash changes */}
                        {(event.previousHash || event.newHash) && (
                          <div className="mt-3 p-3 bg-gray-50 rounded text-xs">
                            {event.previousHash && (
                              <div className="mb-2">
                                <span className="text-gray-500">Previous Hash: </span>
                                <span className="font-mono text-gray-700">{event.previousHash.slice(0, 32)}...</span>
                              </div>
                            )}
                            {event.newHash && (
                              <div>
                                <span className="text-gray-500">New Hash: </span>
                                <span className="font-mono text-gray-700">{event.newHash.slice(0, 32)}...</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Event Types</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(EVENT_ICONS).map(([type, Icon]) => (
            <div key={type} className="flex items-center gap-2">
              <div className={`p-1.5 rounded ${EVENT_COLORS[type]}`}>
                <Icon className="h-3 w-3" />
              </div>
              <span className="text-sm text-gray-600 capitalize">
                {type.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
